'use client'

import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  Trash2Icon,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/cn'

import type { LocalAttachment } from './workflowAttachment/workflowAttachment'

export type WorkflowGateTaskResponsible = {
  id: string
  name: string
  avatar?: string | null
  area?: string | null
}

export type WorkflowGateTask = {
  id?: string
  name: string
  sequence?: number
  endDate?: Date
  responsible?: WorkflowGateTaskResponsible
}

export type WorkflowGateStep = {
  name: string
  tasks: WorkflowGateTask[]
  attachments?: LocalAttachment[]
}

export type WorkflowViewGatesProps = {
  steps: WorkflowGateStep[]
  selectedIndex: number | null
  onSelectGate: (index: number) => void
  onUpdateStepName?: (index: number, name: string) => void
  onDeleteGate?: (index: number) => void
}

const GATE_ARROW_CLIP_PATH_FIRST =
  'polygon(0% 0%, calc(100% - 15px) 0%, 100% 50%, calc(100% - 15px) 100%, 0% 100%)'

const GATE_ARROW_CLIP_PATH =
  'polygon(0% 0%, calc(100% - 15px) 0%, 100% 50%, calc(100% - 15px) 100%, 0% 100%, 15px 50%)'

type GateProps = {
  name: string
  isCompleted: boolean
  isFirst: boolean
  index: number
  isSelected: boolean
  onClick: () => void
  onDelete?: (index: number) => void
}

function Gate({
  name,
  isFirst,
  isSelected,
  isCompleted,
  onClick,
  index,
  onDelete,
}: GateProps) {
  const [isHovered, setIsHovered] = useState(false)
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const clipPath = isFirst ? GATE_ARROW_CLIP_PATH_FIRST : GATE_ARROW_CLIP_PATH

  useEffect(
    () => () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current)
    },
    [],
  )

  const handleMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
    setIsHovered(true)
  }

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => setIsHovered(false), 500)
  }

  return (
    <div
      className="relative min-w-[200px] shrink-0"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={cn(
          'absolute inset-0 transition-colors',
          isSelected ? 'bg-system-primary-purple' : 'bg-transparent',
        )}
        style={{
          clipPath,
          WebkitClipPath: clipPath,
        }}
      />
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'relative flex w-full items-center justify-center gap-1.5 py-3 font-inter text-sm font-medium leading-[1.21] outline-none transition-colors',
          'bg-system-background-primary text-system-text-primary',
          isFirst && 'pl-4 pr-6',
          !isFirst && 'pl-6 pr-6',
        )}
        style={{
          clipPath,
          WebkitClipPath: clipPath,
          margin: isSelected ? '1px' : '0',
          width: isSelected ? 'calc(100% - 2px)' : '100%',
          height: isSelected ? 'calc(100% - 2px)' : '100%',
        }}
      >
        <div className="flex min-w-0 flex-1 items-center justify-center gap-1.5">
          {isCompleted && (
            <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-system-state-success">
              <CheckIcon className="size-4 text-white" />
            </div>
          )}
          <span
            className={cn(
              'truncate',
              isSelected && 'text-system-primary-purple',
            )}
          >
            {name || 'Nome do gate'}
          </span>
        </div>
        {isSelected ? (
          <ChevronUpIcon
            className={cn(
              'size-4 shrink-0',
              isSelected && 'text-system-primary-purple',
            )}
          />
        ) : (
          <ChevronDownIcon className="size-4 shrink-0 text-current opacity-80" />
        )}
      </button>
      {onDelete && (
        <div
          className={cn(
            'absolute top-full z-10 mt-1 transition-opacity',
            isHovered ? 'opacity-100' : 'pointer-events-none opacity-0',
          )}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onDelete(index)
            }}
            className="flex size-6 items-center justify-center rounded-sm border bg-system-background-base text-system-state-error hover:bg-system-state-error/10"
            aria-label="Excluir estágio"
          >
            <Trash2Icon className="size-4" />
          </button>
        </div>
      )}
    </div>
  )
}

Gate.displayName = 'Gate'

type WorkflowGatesProps = {
  steps: WorkflowGateStep[]
  selectedIndex: number | null
  onSelectGate: (index: number) => void
  onDeleteGate?: (index: number) => void
}

export function WorkflowGates({
  steps,
  selectedIndex,
  onSelectGate,
  onDeleteGate,
}: WorkflowGatesProps) {
  if (steps.length === 0) {
    return (
      <div className="flex items-center gap-1 rounded-lg border border-dashed border-system-border-secondary bg-system-background-grey px-4 py-3">
        <span className="text-sm font-medium text-system-text-secondary">
          Nenhum estágio ainda. Clique em &quot;Criar estágio&quot; para
          adicionar.
        </span>
      </div>
    )
  }

  return (
    <div className="flex h-[5.5rem] items-start overflow-x-auto overflow-y-hidden">
      {steps.map((step, index) => (
        <Gate
          key={step.name + index}
          name={step.name}
          index={index}
          isFirst={index === 0}
          isSelected={selectedIndex === index}
          onClick={() => onSelectGate(index)}
          onDelete={onDeleteGate}
          isCompleted={false}
        />
      ))}
    </div>
  )
}

WorkflowGates.displayName = 'WorkflowGates'
